import React from 'react';
import { Platform, StyleSheet, View, type StyleProp, type ViewProps, type ViewStyle } from 'react-native';
import { BlurView } from 'expo-blur';

/**
 * Матовая подложка панели, под которой проезжает содержимое.
 *
 * <p><b>Зачем.</b> Обе платформы пришли к одному: панель не отрезает экран непрозрачной
 * полосой, а лежит НАД содержимым полупрозрачным материалом — так видно, что список
 * продолжается под ней, и экран не распадается на несвязанные прямоугольники. Раньше шапка
 * «Чатов» стояла в колонке над списком: между ними была граница, и никакого «под» не было
 * вовсе — размывать было нечего.
 *
 * <p><b>Про откат.</b> `expo-blur` — нативный модуль. Сборка, собранная ДО его появления в
 * зависимостях, нативной части не содержит, и `BlurView` в ней не нарисует ничего. Поэтому
 * размытие всегда лежит поверх обычной полупрозрачной заливки: если модуль на месте —
 * получается матовое стекло, если нет — просто полупрозрачная панель. Читаемость подписей
 * держит заливка, а не размытие, поэтому во втором случае ломается только красота.
 *
 * <p><b>Android.</b> Там `BlurView` без экспериментального способа размытия не размывает, а
 * только тонирует: полупрозрачная панель пропускала строки списка сквозь имя и чипы фильтров
 * (e2e LIST-HEADER-BLEED). На Android заливка глухая — тот же цвет без прозрачности.
 */
export function FrostedBar({
  tint,
  fallbackColor,
  intensity = 40,
  style,
  onLayout,
  children,
}: {
  /** Светлая панель на светлой теме, тёмная — на тёмной. */
  tint: 'light' | 'dark';
  /** Полупрозрачная заливка под размытием — она же единственный фон там, где размытия нет. */
  fallbackColor: string;
  intensity?: number;
  style?: StyleProp<ViewStyle>;
  /** Высота панели нужна вызывающей стороне: под неё отводится верхний отступ содержимого. */
  onLayout?: ViewProps['onLayout'];
  children: React.ReactNode;
}) {
  if (Platform.OS === 'android') {
    return (
      <View style={style} onLayout={onLayout}>
        <View
          style={[StyleSheet.absoluteFill, { backgroundColor: opaque(fallbackColor) }]}
          pointerEvents="none"
        />
        {children}
      </View>
    );
  }
  return (
    <View style={style} onLayout={onLayout}>
      <View
        style={[StyleSheet.absoluteFill, { backgroundColor: fallbackColor }]}
        pointerEvents="none"
      />
      <BlurView
        intensity={intensity}
        tint={tint}
        style={StyleSheet.absoluteFill}
        pointerEvents="none"
      />
      {children}
    </View>
  );
}

/** `rgba(r, g, b, a)` → `rgb(r, g, b)`; другие записи цвета — как есть. */
export function opaque(color: string): string {
  const m = /^rgba\(\s*([^,]+),\s*([^,]+),\s*([^,]+),\s*[^)]+\)$/.exec(color.trim());
  return m ? `rgb(${m[1].trim()}, ${m[2].trim()}, ${m[3].trim()})` : color;
}
